/**
 * Saker's task states on the server (#286 S14, S15, S18; backend/controllers/admin/sakerController.js): a
 * task put off to a date, taken up, finished or reopened, for the whole company, each change with who, when
 * and why. The latest row per task and team is the state; the list holds the ones put off or finished now.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, get, post } from './client';

export type TaskAction = 'defer' | 'resume' | 'done' | 'reopen';
export interface TaskState {
  id: number;
  key: string;
  /** the team it was decided for, null for Alle team */
  team: string | null;
  action: TaskAction;
  before: 'open' | 'deferred' | 'done';
  /** the day to follow up, YYYY-MM-DD, for a deferral */
  due: string | null;
  /** the day the task was about */
  date: string | null;
  area: string | null;
  reason: string | null;
  at: string;
  user_id: number | null;
  /** who decided, by name */
  by: string | null;
}

export const SAKER_STATES = ['saker-states'];

export function useSakerStates(enabled = true) {
  return useQuery({
    queryKey: SAKER_STATES,
    enabled,
    queryFn: async () => {
      const r = await get<{ states?: TaskState[] }>('/admin/saker/states');
      return Array.isArray(r?.states) ? r.states : [];
    },
    staleTime: 30_000,
  });
}

export interface TaskWrite {
  key: string;
  action: TaskAction;
  team?: string | null;
  due?: string;
  date?: string | null;
  area?: string;
  reason?: string;
  /** the id of the latest row seen for this task and team, null for none (S18) */
  expected: number | null;
}

export type TaskWriteResult =
  | { ok: true; row: TaskState }
  | { ok: false; conflict: boolean; message: string; current?: TaskState | null };

const rid = () =>
  globalThis.crypto?.randomUUID?.() ??
  `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * One decision, sent with one request id: when no answer came back (the network, not a refusal), the same
 * request is sent once more, which the server answers with the row it already wrote, never a second one.
 */
export function useSakerWrite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (w: TaskWrite): Promise<TaskWriteResult> => {
      const body = { ...w, team: w.team ?? null, request_id: rid() };
      const once = () => post<TaskState>('/admin/saker/states', body);
      try {
        let row: TaskState;
        try {
          row = await once();
        } catch (e) {
          if (e instanceof ApiError) throw e;
          row = await once();
        }
        return { ok: true, row };
      } catch (e) {
        if (e instanceof ApiError) {
          const current = (e.body as { current?: TaskState | null } | undefined)?.current;
          return { ok: false, conflict: e.status === 409, message: e.message, current: current ?? null };
        }
        return { ok: false, conflict: false, message: '' }; // unknown: the panel says so in its own words
      }
    },
    // the row the server wrote counts at once (it is not a guess: the server answered it), then the list is read again
    onSuccess: (r) => {
      if (r.ok) qc.setQueryData<TaskState[]>(SAKER_STATES, (old) => [...(old ?? []), r.row]);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: SAKER_STATES }),
  });
}
